import { Component, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { BaseInputComponent } from '../base-input/base-input.component';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { isEqual } from 'lodash';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { Observable, startWith, map, Subject, merge, of, switchMap } from 'rxjs';
import { SortDirection } from '../../../../../../../api/src/_core/utils/utils.paging';
import { catchError } from 'rxjs/operators';
import { BaseEntityService } from '../../../../_core/state/base-entity.service';
import { QueryParams } from '../../../../_core/state/collection/collection.model';
import { InputUtils } from "../input.utils";

@Component({
  selector: 'app-multiselect-input',
  templateUrl: './multiselect-input.component.html',
  styleUrls: ['./multiselect-input.component.scss']
})
export class MultiselectInputComponent extends BaseInputComponent implements OnInit {
	protected readonly InputUtils = InputUtils;
	protected readonly InputPlaceholder = $localize`:Input Placeholder@@inputPlaceholder:New item...`;

	@ViewChild('itemInput') itemInput: any;
	@Input() allowCustomValues: boolean = false;
	@Input() selectRemoved: boolean = false;
	@Input() readonly : boolean = false;
	@Input() order: SortDirection | null = null;
	@Input() prepareValueFn: (args: any) => any = null;
	@Input() enableRemoteSearch: boolean = false;
	@Input() remoteSearchService: BaseEntityService;
	@Input() remoteSearchPageSize: number = 10;
	@Input() sublineKey: string | null = null; // Key for the subline text in the options

	private remoteSearchQuery: string = '';

	public separatorKeysCodes: number[] = [ENTER, COMMA];
	public itemCtrl = new UntypedFormControl();
	public filteredItems$: Observable<any[]>;

	private _options$ = new Subject<void>();

	constructor() {
		super();

		this.filteredItems$ = merge(this._options$, this.itemCtrl.valueChanges).pipe(
			startWith(null),
			switchMap((item: string | null) => {
				if (this.enableRemoteSearch && this.remoteSearchService) {
					this.remoteSearchQuery = item || '';
					return this.fetchRemoteOptions(this.remoteSearchQuery);
				} else {
					return of(this.filterLocalOptions(item));
				}
			}),
			map((options: any[]) => InputUtils.conditionalSort(options, this.order, this.optionValueKey))
		  );
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes.options || changes.currentValue) {
			this._options$.next();
		}
	}

	ngOnInit() {
		super.ngOnInit();

		if (!this.parentForm) {
			return;
		}

		if (this.controlName) {
			const control = this.parentForm.get(this.controlName) as FormControl;

			if (control) {
				control.setValidators(this.validators || []);
				control.updateValueAndValidity();
			}
		}
	}

	add(event: MatChipInputEvent): void {
		if (this.parentForm) {
			/* manually mark the form control */
			(this.parentForm.get(this.controlName) as FormControl).markAsTouched();
		}

		let value = event.value;

		if (!this.allowCustomValues) {
			// Clear the input value
			event.chipInput!.clear();
			this.itemCtrl.setValue(null);
			return;
		}

		// Add our item
		if (value) {
			if (this.prepareValueFn) {
				value = this.prepareValueFn(value)
			}
			this.form.patchValue({
				[this.controlName]: [...(this.form.value[this.controlName] || []), value]
			});
		}

		// Clear the input value
		event.chipInput!.clear();
		this.itemCtrl.setValue(null);
	}

	remove(item: any): void {
		const newValue = this.form.value[this.controlName]
			.filter((i: any) => !isEqual(item, i));

		this.form.patchValue({
			[this.controlName]: newValue
		});

		// also update the filtered items to current value
		if (this.selectRemoved) {
			if (this.optionValueKey) {
				this.itemInput.nativeElement.value = item[this.optionValueKey];
				this.itemCtrl.setValue(item[this.optionValueKey]);
			} else {
				this.itemInput.nativeElement.value = item;
				this.itemCtrl.setValue(item);
			}
		} else {
			this.itemInput.nativeElement.value = '';
			this.itemCtrl.setValue(null);
		}
	}

	selected(event: MatAutocompleteSelectedEvent): void {
		const newValue = (this.form.value[this.controlName] || []).concat(event.option.value);
		this.form.patchValue({
			[this.controlName]: newValue
		});

		this.itemInput.nativeElement.value = '';
		this.itemCtrl.setValue(null);
	}

	hasError(): boolean {
		if (!this.parentForm) {
			return false;
		}

		if (this.controlName) {
			const control = this.parentForm.get(this.controlName) as FormControl;

			return control && control.invalid && control.touched;
		}
		return false;
	}

	private filterLocalOptions(item: string | null): any[] {
		const items = this.form.value[this.controlName] || [];
		return this.filterOptions(item, this.options || []);
	}

	private fetchRemoteOptions(query: string): Observable<any[]> {
		const params: QueryParams = {
			page: 1,
			limit: this.remoteSearchPageSize,
			sortBy: null,
			query
		};

		return this.remoteSearchService.get(params).pipe(
			map((response: any) => this.filterOptions(query, response.data || [], true)),
			catchError(() => of([]))
		);
	}

	filterOptions(item: string | null, options: any[], onlySelected: boolean = false): any[] {
		const items = this.form.value[this.controlName] || [];
		return (options || []).filter((option: any) => {
			let isAcceptedByFilter: boolean, isAlreadySelected: boolean;
			if (this.optionValueKey) {
				if (!onlySelected) {
					isAcceptedByFilter = item
						? option[this.optionValueKey].toLowerCase().includes(item[this.optionValueKey]?.toLowerCase() || item.toLowerCase())
						: true;
				} else {
					isAcceptedByFilter = true;
				}

				isAlreadySelected = items.some((item: any) => isEqual(item[this.optionValueKey], option[this.optionValueKey]));
			} else {
				if (!onlySelected) {
					isAcceptedByFilter = item ? isEqual(item, option) : true;
				} else {
					isAcceptedByFilter = true;
				}

				isAlreadySelected = items.some((item: any) => isEqual(item, option));
			}
			return !isAlreadySelected && isAcceptedByFilter;
		});
	}
}
